/**
 * Map Event Engine — Tuxemon-style condition/action triggers.
 * Supports player position, item, variable, and NPC-facing triggers.
 */

import type { Position } from "./constants";

export type TriggerType =
  | "player_position"
  | "player_facing_npc"
  | "has_item"
  | "has_surggie"
  | "variable_set"
  | "time_of_day";

export type ActionType =
  | "show_dialog"
  | "give_item"
  | "remove_item"
  | "set_variable"
  | "spawn_surggie"
  | "teleport"
  | "play_animation"
  | "show_battle"
  | "grant_xp";

export interface MapCondition {
  type: TriggerType;
  x?: number;
  y?: number;
  npc_id?: string;
  item?: string;
  surggie_id?: string;
  variable?: string;
  value?: boolean | string | number;
  time_of_day?: "morning" | "afternoon" | "evening" | "night";
}

export interface MapAction {
  type: ActionType;
  text?: string;
  item?: string;
  variable?: string;
  value?: boolean | string | number;
  surggie_id?: string;
  encounter?: boolean;
  x?: number;
  y?: number;
  animation?: string;
  xp_amount?: number;
  amount?: number;
}

export interface MapEvent {
  id: string;
  trigger: MapCondition;
  conditions?: MapCondition[];
  actions: MapAction[];
  once?: boolean;
  cooldown_ms?: number;
}

interface EventState {
  triggered: boolean;
  lastTriggered: number;
}

export class MapEventEngine {
  private events: MapEvent[] = [];
  private eventStates: Map<string, EventState> = new Map();
  private gameVariables: Map<string, boolean | string | number> = new Map();
  private inventory: Set<string> = new Set();
  private playerPosition: Position = { x: 0, y: 0 };
  private facingNpcId: string | null = null;

  /**
   * Load events from an array (typically parsed from JSON/YAML).
   */
  loadEvents(events: MapEvent[]): void {
    this.events = events;
  }

  /**
   * Update player position for position-based triggers.
   */
  setPlayerPosition(x: number, y: number): void {
    this.playerPosition = { x, y };
  }

  /**
   * Set which NPC the player is currently facing (for player_facing_npc triggers).
   */
  setFacingNpc(npcId: string | null): void {
    this.facingNpcId = npcId;
  }

  /**
   * Set a game variable (for variable_set triggers).
   */
  setVariable(key: string, value: boolean | string | number): void {
    this.gameVariables.set(key, value);
  }

  /**
   * Get a game variable.
   */
  getVariable(key: string): boolean | string | number | undefined {
    return this.gameVariables.get(key);
  }

  /**
   * Add item to inventory (for has_item checks).
   */
  addItem(item: string): void {
    this.inventory.add(item);
  }

  /**
   * Remove item from inventory.
   */
  removeItem(item: string): void {
    this.inventory.delete(item);
  }

  /**
   * Check if player has an item.
   */
  hasItem(item: string): boolean {
    return this.inventory.has(item);
  }

  /**
   * Evaluate all active triggers and return actions to execute.
   * Call this on player move or interaction.
   */
  evaluate(): MapAction[] {
    const now = Date.now();
    const triggeredActions: MapAction[] = [];

    for (const event of this.events) {
      const state = this.eventStates.get(event.id);

      // Check if already triggered (once-only)
      if (event.once && state?.triggered) continue;

      // Check cooldown
      if (event.cooldown_ms && state?.lastTriggered) {
        if (now - state.lastTriggered < event.cooldown_ms) continue;
      }

      // Check main trigger
      if (!this.checkCondition(event.trigger)) continue;

      // Check additional conditions
      let allConditionsMet = true;
      if (event.conditions) {
        for (const cond of event.conditions) {
          if (!this.checkCondition(cond)) {
            allConditionsMet = false;
            break;
          }
        }
      }

      if (!allConditionsMet) continue;

      // Trigger event
      triggeredActions.push(...event.actions);
      this.eventStates.set(event.id, {
        triggered: true,
        lastTriggered: now,
      });
    }

    return triggeredActions;
  }

  /**
   * Check a single condition.
   */
  private checkCondition(cond: MapCondition): boolean {
    switch (cond.type) {
      case "player_position":
        if (cond.x === undefined || cond.y === undefined) return false;
        const dx = Math.abs(this.playerPosition.x - cond.x * 16);
        const dy = Math.abs(this.playerPosition.y - cond.y * 16);
        return dx < 16 && dy < 16;

      case "has_item":
        return cond.item ? this.inventory.has(cond.item) : false;

      case "has_surggie":
        return cond.surggie_id ? true : false; // checked externally

      case "variable_set":
        if (!cond.variable) return false;
        const val = this.gameVariables.get(cond.variable);
        if (cond.value === undefined) return val !== undefined;
        return val === cond.value;

      case "time_of_day": {
        const hour = new Date().getHours();
        let currentPeriod: string;
        if (hour >= 6 && hour < 12) currentPeriod = "morning";
        else if (hour >= 12 && hour < 17) currentPeriod = "afternoon";
        else if (hour >= 17 && hour < 21) currentPeriod = "evening";
        else currentPeriod = "night";
        return cond.time_of_day === currentPeriod;
      }

      case "player_facing_npc":
        return cond.npc_id ? this.facingNpcId === cond.npc_id : false;

      default:
        return false;
    }
  }

  /**
   * Execute an action. Returns result description.
   */
  executeAction(action: MapAction): string | null {
    switch (action.type) {
      case "show_dialog":
        return action.text ?? null;

      case "give_item":
        if (action.item) {
          this.addItem(action.item);
          return `Received ${action.item}!`;
        }
        return null;

      case "remove_item":
        if (action.item) {
          this.removeItem(action.item);
          return `Used ${action.item}.`;
        }
        return null;

      case "set_variable":
        if (action.variable !== undefined && action.value !== undefined) {
          this.setVariable(action.variable, action.value);
          return `Variable ${action.variable} set.`;
        }
        return null;

      case "spawn_surggie":
        return action.surggie_id
          ? `Wild ${action.surggie_id} appeared!`
          : null;

      case "teleport":
        return action.x !== undefined && action.y !== undefined
          ? `Teleported to (${action.x}, ${action.y})`
          : null;

      case "play_animation":
        return action.animation
          ? `Playing animation: ${action.animation}`
          : null;

      case "show_battle":
        return "Battle started!";

      case "grant_xp":
        return action.xp_amount
          ? `+${action.xp_amount} XP!`
          : null;

      default:
        return null;
    }
  }

  /**
   * Execute multiple actions.
   */
  executeActions(actions: MapAction[]): string[] {
    const results: string[] = [];
    for (const action of actions) {
      const result = this.executeAction(action);
      if (result) results.push(result);
    }
    return results;
  }

  /**
   * Reset all event states (for new game).
   */
  reset(): void {
    this.eventStates.clear();
    this.gameVariables.clear();
    this.inventory.clear();
  }
}

export const globalEventEngine = new MapEventEngine();
